import { SgTimepicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Checkbox } from '@sgme/ui';
import { format } from 'date-fns';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useAlgoSetIsModifyingField,
  useAlgoTileId,
  useValidateAlgo,
} from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { SgDatePicker } from '@/components/sg-design-system/components/input/SgDatePicker/index.tsx';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectAlgoErrors,
  selectAlgoTile,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromAlgoTile,
} from '@/store/orders/algoOrders/selectors.ts';
import type { ExpiryDate } from '@/store/orders/algoOrders/state.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { DATE_FORMAT_TEXT } from '@/utils/date.ts';

type AlgoEditingExpiryDateProps = {
  expiryDate: ExpiryDate | undefined;
  setExpiryDate: (value: ExpiryDate | undefined) => void;
};

export const AlgoEditingExpiryDate = ({ expiryDate, setExpiryDate }: AlgoEditingExpiryDateProps) => {
  const tileId = useAlgoTileId();
  const setIsModifyingField = useAlgoSetIsModifyingField();
  const track = useTrackEvent();

  const { formatMessage, formatTime, locale } = useIntl();
  const { dateInputCultureInfo } = useAppSelector(getUserPreferences);

  const tile = useAppSelector((state) => selectAlgoTile(state, tileId));

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const [time, setTime] = useState(
    isDefined(orderFromBlotter?.endTime)
      ? format(new Date(orderFromBlotter.endTime), 'HH:mm:ss')
      : format(new Date(), 'HH:mm:ss'),
  );
  const [shouldDisplayGoodTillDate, setShouldDisplayGoodTillDate] = useState(
    tile?.properties.expiryDate.goodTillDate ?? false,
  );

  const errors = useAppSelector((state) => selectAlgoErrors(state, tileId));

  const validateAlgo = useValidateAlgo();

  const handleIsNowChange = () => {
    track('Order action', 'Is Now changed');
    if (isNotDefined(orderFromBlotter?.endTime)) {
      return;
    }
    const date = format(orderFromBlotter?.endTime, 'yyyy-MM-dd');
    setShouldDisplayGoodTillDate(false);
    validateAlgo({ expiryDate: { goodTillDate: false, date: date, time } });
  };

  const handleDateChange = async (date: string) => {
    track('Order action', 'Expiry date changed');
    if (isNotDefined(orderFromBlotter) || isNotDefined(date)) {
      return;
    }

    const response = await validateAlgo({
      expiryDate: {
        goodTillDate: false,
        date,
        time: time,
      },
    });

    if (response?.data?.validationResponseDetailContent.length === 0) {
      setExpiryDate({
        goodTillDate: false,
        date: date,
        time: time,
      });
    }
  };

  const handleTimeChange = async ({
    hour = 0,
    minute = 0,
    second = 0,
    period,
  }: {
    hour?: number;
    minute?: number;
    second?: number;
    period?: 'AM' | 'PM';
  }) => {
    track('Order action', 'Expiry time changed');
    if (isNotDefined(orderFromBlotter)) {
      return;
    }

    const hour24 = period === 'PM' && hour < 12 ? hour + 12 : hour;

    if (isDefined(orderFromBlotter.endTime)) {
      const newTime = formatTime(new Date(0, 0, 0, hour24, minute, second), { timeStyle: 'medium', hour12: false });
      const date = format(orderFromBlotter.endTime, 'yyyy-MM-dd');

      setTime(newTime);
      const response = await validateAlgo({
        expiryDate: {
          goodTillDate: false,
          date: date,
          time: newTime,
        },
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setExpiryDate({
          goodTillDate: false,
          date: date,
          time: newTime,
        });
      }
    }
  };

  const getComputedLocale = () => {
    if (dateInputCultureInfo === 'en') {
      return 'en-US';
    }
    if (dateInputCultureInfo === 'fr') {
      return 'fr-FR';
    }
    if (locale === 'en') {
      return 'en-GB';
    }
    return 'fr-FR';
  };

  const expiryDateDateError = errors?.expiryDate;

  const endTime = isDefined(orderFromBlotter?.endTime) ? format(orderFromBlotter.endTime, 'yyyy-MM-dd') : undefined;

  const expiryDateValue = isDefined(expiryDate?.date) ? format(expiryDate.date, 'yyyy-MM-dd') : endTime;

  return (
    <OrderRow labelId="order.fields.expiryDate.label" alignTop>
      {shouldDisplayGoodTillDate ? (
        <Checkbox
          checked={shouldDisplayGoodTillDate}
          onCheckBoxChange={handleIsNowChange}
          label={formatMessage({ id: 'order.fields.expiryDate.goodTillDate' })}
          size="sm"
        />
      ) : (
        <div
          className="d-flex flex-column flex-nowrap  gap-4px align-items-between justify-content-between flex-grow-0 flex-shrink-0"
          data-e2e="expiry-date"
        >
          <OmsFieldErrorWrapper errors={expiryDateDateError}>
            <SgDatePicker
              size="sm"
              yearControls
              value={expiryDateValue}
              locale={locale === 'en' ? 'en-GB' : 'fr-FR'}
              dateInputCultureInfo={getComputedLocale()}
              placeholder="expiry date"
              onChange={handleDateChange}
              onFocus={() => setIsModifyingField(true)}
              onBlurInput={() => setIsModifyingField(false)}
              showClearButton={false}
              validationState={isDefined(expiryDateDateError) ? 'invalid' : undefined}
              dateFormat={DATE_FORMAT_TEXT}
            />
          </OmsFieldErrorWrapper>

          <SgTimepicker
            size="sm"
            appendTo="body"
            withSeconds
            onTimeSelected={(e) => handleTimeChange(e.detail)}
            value={time}
          />
        </div>
      )}
    </OrderRow>
  );
};
